import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { KeyFilterModule } from 'primeng/keyfilter';
import { Concrete, Measurement } from '@cosmos/shared';

function concreteLabel(concrete: Concrete): string {
  return `${concrete.name} (${concrete['concreteMixture']?.['name'] || ''} - ${concrete['concreteMixture']?.['concreteType'] || ''})`;
}

@Component({
  selector: 'cosmos-measurement-change-concrete-dialog',
  templateUrl: './measurement-change-concrete-dialog.component.html',
  imports: [FormsModule, TranslatePipe, SelectModule, ButtonModule, MessageModule, AutoCompleteModule, KeyFilterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasurementChangeConcreteDialogComponent {
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);
  private readonly translateService = inject(TranslateService);

  readonly measurement: Measurement = this.config.data.measurement;
  readonly concretes: Concrete[] = this.config.data.concretes;

  readonly currentConcreteId: string = (this.measurement.concrete as unknown as Concrete)?._id ?? (this.measurement.concrete as unknown as string);

  readonly concreteOptions = computed(() => this.concretes.map(c => ({ value: c._id, label: concreteLabel(c) })));

  readonly selectedConcreteId = signal<string>(this.currentConcreteId);

  readonly currentConcrete = computed(() => this.concretes.find(c => c._id === this.currentConcreteId));
  readonly selectedConcrete = computed(() => this.concretes.find(c => c._id === this.selectedConcreteId()));
  readonly hasChanged = computed(() => this.selectedConcreteId() !== this.currentConcreteId);

  private readonly originalSpecifications: number[] = this.measurement.specifications ?? [];
  private readonly originalSpecificationsMaturity: number[] = this.measurement.specifications_maturity ?? [];
  private readonly originalSpecificationsMaturityRelative: number[] = this.measurement.specifications_maturity_relative ?? [];

  readonly specifications = signal<number[]>(this.originalSpecifications);
  readonly specificationsMaturity = signal<number[]>(this.originalSpecificationsMaturity);
  readonly specificationsMaturityRelative = signal<number[]>(this.originalSpecificationsMaturityRelative);

  /** True once specs are copied from the newly selected concrete or hand-edited — drives the "Restore original specs" affordance. */
  readonly specsChanged = signal(false);

  onSpecificationsChange(value: unknown) {
    this.specifications.set(this.normalizeChips(value));
    this.specsChanged.set(true);
  }

  onSpecificationsMaturityChange(value: unknown) {
    this.specificationsMaturity.set(this.normalizeChips(value));
    this.specsChanged.set(true);
  }

  onSpecificationsMaturityRelativeChange(value: unknown) {
    this.specificationsMaturityRelative.set(this.normalizeChips(value));
    this.specsChanged.set(true);
  }

  private normalizeChips(value: unknown): number[] {
    if (!Array.isArray(value)) return [];
    return value
      .map(item => Number(item))
      .filter((item, index, self) => !isNaN(item) && self.indexOf(item) === index)
      .sort((a, b) => a - b);
  }

  copySpecificationsFromSelected() {
    const selected = this.selectedConcrete();
    if (!selected) return;
    this.specifications.set(selected.specifications ?? []);
    this.specificationsMaturity.set(selected.specifications_maturity ?? []);
    this.specificationsMaturityRelative.set(selected.specifications_maturity_relative ?? []);
    this.specsChanged.set(true);
  }

  restoreOriginalSpecifications() {
    this.specifications.set(this.originalSpecifications);
    this.specificationsMaturity.set(this.originalSpecificationsMaturity);
    this.specificationsMaturityRelative.set(this.originalSpecificationsMaturityRelative);
    this.specsChanged.set(false);
  }

  confirm() {
    this.ref.close({
      concrete: this.selectedConcreteId(),
      specifications: this.specifications(),
      specifications_maturity: this.specificationsMaturity(),
      specifications_maturity_relative: this.specificationsMaturityRelative(),
    });
  }

  cancel() {
    this.ref.close(undefined);
  }
}
